import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";

export const caseStudies = [
  {
    id: "cdc-tracking",
    sector: "Environmental Health Surveillance",
    headline_value: "400+",
    headline_label: "Public health actions accepted by CDC",
    secondary: "~70% of reported activities met CDC's program criteria across 11 years (2005–2016).",
    context:
      "CDC's National Environmental Public Health Tracking Program demonstrates how systematic, indicator-based surveillance translates into accepted, fundable public health actions at the state and local level.",
    methods: ["Indicator dashboards", "Cross-jurisdiction reporting", "Performance measurement"],
    source: "CDC / PMC peer-reviewed (PMC5672914)",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5672914/",
    relevance: "Mirrors our approach to Data Analysis & Outcomes Measurement engagements.",
  },
  {
    id: "rwjf-finding-answers",
    sector: "Health Equity & Disparities",
    headline_value: "Multi-site",
    headline_label: "Disparities reductions in diabetes, depression & CVD",
    secondary:
      "Evaluations across 33 grantee programs showed measurable disparity reductions via community health workers, culturally-tailored care, and integrated care models.",
    context:
      "The RWJF 'Finding Answers' initiative is the canonical evidence base for how to design and evaluate equity-focused interventions that move the needle on racial/ethnic outcome gaps.",
    methods: ["CHW models", "Cultural tailoring", "Audit & feedback", "Patient registries"],
    source: "Robert Wood Johnson Foundation / PMC (PMC2745903)",
    source_url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2745903/",
    relevance: "Mirrors our Health Equity Evaluation methodology.",
  },
  {
    id: "nv-heap",
    sector: "Statewide Health Equity Infrastructure",
    headline_value: "6",
    headline_label: "Priority focus areas across Nevada",
    secondary:
      "Data improvement · organizational capacity · community partnerships · language access · policy change · emergency preparedness.",
    context:
      "Nevada's Health Equity Action Plan (HEAP) builds federally-supported workforce capacity and statewide infrastructure for measuring and reducing disparities — the exact kind of multi-level program our evaluations support.",
    methods: ["Statewide indicator frameworks", "SDOH integration", "Capacity assessments"],
    source: "Nevada Department of Health & Human Services (Office of Minority Health & Equity)",
    source_url: "https://www.dhs.nv.gov/siteassets/content/programs/nomhe/mh/Health_Equity_Action_Plan_FINAL_ADA_cover.pdf",
    relevance: "Aligned with our Community Health Assessment & equity evaluation work.",
  },
  {
    id: "tfah-funding-roi",
    sector: "Public Health ROI",
    headline_value: "$5.4B",
    headline_label: "Projected GDP loss from CDC budget cuts",
    secondary:
      "≈ 42,000 jobs and $240M+ in state/local tax revenue at risk — quantifying what's lost without rigorous public health programs and the evaluations that defend their funding.",
    context:
      "GWU and Trust for America's Health (2024) estimated the downstream economic impact of weakening federal public health funding. Strong evaluation evidence is what protects program budgets in this climate.",
    methods: ["Economic impact modeling", "Funder-ready evaluation plans", "Performance dashboards"],
    source: "Trust for America's Health (TFAH) 2024 · GWU Milken Institute SPH",
    source_url: "https://www.tfah.org/report-details/funding-2024/",
    relevance: "Why our Grant Evaluation Plans pay for themselves many times over.",
  },
];

function CaseStudyCard({ cs, large = false }) {
  return (
    <article
      className={`card-dark p-8 flex flex-col ${large ? "lg:p-10" : ""}`}
      data-testid={`case-study-${cs.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <span className="overline text-[var(--silver)]">{cs.sector}</span>
        <a
          href={cs.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--text-3)] hover:text-[var(--red)] transition-colors"
          aria-label="Open source"
          data-testid={`case-source-${cs.id}`}
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className={`font-display font-semibold leading-none tracking-tight text-[var(--red)] ${large ? "text-7xl lg:text-8xl" : "text-6xl"}`}>
        {cs.headline_value}
      </div>
      <div className="mt-3 text-sm uppercase tracking-[0.18em] text-[var(--text-2)]">
        {cs.headline_label}
      </div>

      <p className="mt-6 text-[var(--text-2)] leading-relaxed">{cs.secondary}</p>

      {large && (
        <p className="mt-5 text-sm text-[var(--text-3)] leading-relaxed">{cs.context}</p>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {cs.methods.map((m) => (
          <span
            key={m}
            className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border border-[var(--border)] text-[var(--text-2)]"
          >
            {m}
          </span>
        ))}
      </div>

      <div className="mt-7 pt-5 border-t border-[var(--border)] flex flex-col gap-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)]">Source</div>
        <a
          href={cs.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[var(--silver)] hover:text-white transition-colors break-words"
        >
          {cs.source}
        </a>
        {large && (
          <div className="mt-3 text-xs text-[var(--text-2)] italic">{cs.relevance}</div>
        )}
      </div>
    </article>
  );
}

// Compact 4-up section for the Home page
export function CaseStudiesHomeSection() {
  return (
    <section className="border-y border-[var(--border)] bg-[var(--bg)]" data-testid="home-case-studies">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline text-[var(--red)] mb-3">Impact & Case Studies</div>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl tracking-tight leading-tight">
              The kind of <span className="text-[var(--red)]">outcomes</span> our methodologies are built to deliver.
            </h2>
            <p className="mt-5 text-[var(--text-2)] leading-relaxed">
              Real, published metrics from peer programs and frameworks — illustrating the scale of impact that rigorous evaluation makes possible. Each card is sourced directly from CDC, RWJF, Nevada DHHS, and Trust for America's Health.
            </p>
          </div>
          <Link
            to="/case-studies"
            className="text-[var(--silver)] hover:text-white text-sm inline-flex items-center gap-2 group"
            data-testid="cta-all-case-studies"
          >
            Read full case studies <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {caseStudies.map((cs) => (
            <CaseStudyCard key={cs.id} cs={cs} />
          ))}
        </div>

        <p className="mt-10 text-xs text-[var(--text-3)] max-w-3xl leading-relaxed">
          <span className="text-[var(--silver)] uppercase tracking-[0.18em]">Disclosure:</span>{" "}
          The outcomes above are drawn from published, publicly-available evaluations by external organizations. They illustrate sector benchmarks and methodologies — not engagements completed by Public Health Masters. Client-specific case studies will be added as engagements complete and partner permissions are received.
        </p>
      </div>
    </section>
  );
}

// Full /case-studies page
export default function CaseStudies() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="case-studies-page">
      <div className="py-16">
        <div className="overline text-[var(--red)] mb-4">Impact & Case Studies</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl leading-[1.05]">
          Sector benchmarks that <span className="text-[var(--red)]">define</span> rigorous public health evaluation.
        </h1>
        <p className="mt-6 text-lg text-[var(--text-2)] max-w-3xl">
          Real outcomes and funding figures from peer-reviewed evaluations and authoritative public health bodies. Each entry is publicly cited — these are the kinds of results our methodologies are built to deliver for your programs.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {caseStudies.map((cs) => (
          <CaseStudyCard key={cs.id} cs={cs} large />
        ))}
      </div>

      <section className="mt-20 border border-[var(--border)] bg-[var(--surface)] p-10 lg:p-14 grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2">
          <div className="overline text-[var(--silver)] mb-3">Transparency note</div>
          <h3 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight mb-4">
            Why we cite sector benchmarks — not invented metrics.
          </h3>
          <p className="text-[var(--text-2)] leading-relaxed">
            Public Health Masters is a new, focused evaluation firm. Rather than fabricate client outcomes, we show you the published, peer-reviewed evidence from CDC, RWJF, state health departments, and policy research bodies — so you can judge the methodologies we apply. As partner engagements complete and permissions are received, we will replace these benchmarks with our own client case studies, fully attributed and verified.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Link to="/book-consultation" className="btn-red w-full justify-center" data-testid="cs-book-btn">
            Book a Free Consultation
          </Link>
          <Link to="/services" className="btn-ghost w-full justify-center" data-testid="cs-services-btn">
            See Our Services
          </Link>
        </div>
      </section>
    </div>
  );
}
